/**
 * Application Configuration
 *
 * Centralized configuration using Vite environment variables.
 * Environment variables must be prefixed with VITE_ to be exposed to the client.
 *
 * @see https://vitejs.dev/guide/env-and-mode.html
 */

/**
 * Get backend URL from environment variable or use default
 *
 * Environment variable: VITE_BACKEND_URL
 * Default: http://localhost:9000
 */
export function getBackendUrl(): string {
    return import.meta.env.VITE_BACKEND_URL ?? 'http://localhost:9000'
}

/**
 * Get frontend port from environment variable or use default
 *
 * Environment variable: VITE_PORT
 * Default: 6173
 */
export function getFrontendPort(): number {
    const port = import.meta.env.VITE_PORT
    return port ? parseInt(port, 10) : 6173
}

/**
 * Get Azure AD client ID from environment variable
 *
 * Environment variable: VITE_AZURE_CLIENT_ID
 * Required for admin consent URL generation
 */
export function getAzureClientId(): string {
    return import.meta.env.VITE_AZURE_CLIENT_ID || ''
}

/**
 * Get Azure AD tenant ID from environment variable
 *
 * Environment variable: VITE_AZURE_TENANT_ID
 * Required for Azure AD authorization
 */
export function getAzureTenantId(): string {
    return import.meta.env.VITE_AZURE_TENANT_ID || ''
}

/**
 * Application configuration object
 */
export const config = {
    /**
     * Backend API base URL
     * Configurable via VITE_BACKEND_URL environment variable
     * Default: http://localhost:9000
     */
    backendUrl: getBackendUrl(),

    /**
     * Frontend development server port
     * Configurable via VITE_PORT environment variable
     * Default: 6173
     */
    frontendPort: getFrontendPort(),

    /**
     * Azure AD client ID
     * Configurable via VITE_AZURE_CLIENT_ID environment variable
     * Used for admin consent URL generation
     */
    azureClientId: getAzureClientId(),

    /**
     * Azure AD tenant ID
     * Configurable via VITE_AZURE_TENANT_ID environment variable
     * Used for Azure AD authorization
     */
    azureTenantId: getAzureTenantId(),

    /**
     * Default API timeout in milliseconds
     */
    apiTimeout: 10000,
} as const

/**
 * Test configuration
 * Used by integration and E2E tests
 */
export const testConfig = {
    /**
     * Backend URL for testing
     * Can be overridden with TEST_BACKEND_URL environment variable in tests
     */
    backendUrl: import.meta.env.TEST_BACKEND_URL || getBackendUrl(),

    /**
     * Frontend URL for E2E testing
     */
    frontendUrl: `http://localhost:${getFrontendPort()}`,

    /**
     * Wrong port URL for testing network failures (no mocking allowed per TEST_STRATEGY.md)
     */
    wrongPortUrl: 'http://localhost:9999',

    /**
     * Test timeouts
     */
    networkTimeout: 5000,
    requestTimeout: 30000,
    monacoTimeout: 60000,
} as const

export default config